import { useEffect } from "react";
import { useSettings } from "../context/SettingsContext";

/**
 * Custom hook to set dynamic page titles
 * Format: "{SITE_TITLE} - {PAGE_CONTEXT}"
 *
 * @param pageTitle - The page-specific context (e.g., "Home", "Product Name", "Shopping Cart")
 * @returns The full page title that was set
 *
 * @example
 * // In a component:
 * usePageTitle("Shopping Cart"); // Results in "Deb's Expressions - Shopping Cart"
 *
 * // With dynamic content:
 * usePageTitle(product?.name || "Product"); // Results in "Deb's Expressions - Wind Spinner"
 */
export function usePageTitle(pageTitle: string): string {
  const { settings } = useSettings();
  const siteTitle = settings.site_title || "Deb's Expressions";

  useEffect(() => {
    // Set the full title with site name prefix
    const fullTitle = pageTitle ? `${siteTitle} - ${pageTitle}` : siteTitle;
    document.title = fullTitle;

    // Cleanup: restore to base site title on unmount (optional)
    return () => {
      document.title = siteTitle;
    };
  }, [siteTitle, pageTitle]);

  // Return the full title for components that might need it
  return pageTitle ? `${siteTitle} - ${pageTitle}` : siteTitle;
}
